import { Check, Pencil, Undo2, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import { ErrorDeLaApi } from '../api/nucleo'
import { Aviso, Boton, Casilla, claseDeEntrada, clases, Pastilla, Tarjeta } from '../componentes/ui'
import { type Renombre, type VistaDelPlan, planes } from './api'
import { diferenciasConEdicion, edicionDe, type Elemento, loQueArrastra, seccionesDelPlan } from './revision'

/**
 * «Así propongo organizar tu web» (plan del CMS dinámico §8.3): el plan que propuso la IA,
 * ya revisado por el núcleo, para que la persona lo mire antes de aplicarlo. Cada cosa con su
 * casilla —desmarcarla la quita— y sus nombres editables; lo que el núcleo recortó, con el
 * motivo; y lo que se crea y lo que cambia en la web. Nada se borra de lo que ya hay.
 *
 * Lo usan las dos entradas: el alta (que espera a esta respuesta) y Estructura → «Proponer con IA».
 */
export const RevisionDelPlan = ({ vista, alResolver }: { vista: VistaDelPlan; alResolver: (vista: VistaDelPlan) => void }) => {
  const plan = vista.plan
  const secciones = useMemo(() => (plan ? seccionesDelPlan(plan, vista.tiposDeCampo) : []), [plan, vista.tiposDeCampo])
  const [quitados, setQuitados] = useState<Set<string>>(new Set())
  const [renombres, setRenombres] = useState<Record<string, Renombre>>({})
  const [editando, setEditando] = useState<null | string>(null)
  const [enviando, setEnviando] = useState<'aceptar' | 'descartar' | null>(null)
  const [error, setError] = useState<null | string>(null)

  if (!plan) return <Aviso tono="error">Esta propuesta no trae nada que revisar.</Aviso>
  const diferencias = diferenciasConEdicion(vista.diferencias, plan, quitados, renombres)

  const alternar = (ruta: string) =>
    setQuitados((antes) => {
      const despues = new Set(antes)
      if (despues.has(ruta)) despues.delete(ruta)
      else despues.add(ruta)
      return despues
    })

  const enviar = async (que: 'aceptar' | 'descartar') => {
    setEnviando(que)
    setError(null)
    try {
      alResolver(que === 'aceptar' ? await planes.aceptar(vista.id, edicionDe(plan, quitados, renombres)) : await planes.descartar(vista.id))
    } catch (e) {
      setError(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido enviar. Vuelve a intentarlo.')
    } finally {
      setEnviando(null)
    }
  }

  const fila = (e: Elemento, nivel: 0 | 1) => {
    const quitado = quitados.has(e.ruta) || (nivel === 1 && [...quitados].some((q) => q.startsWith('grupo:') && e.ruta.startsWith(`campo:${q.slice(6)}/`)))
    const valores = { ...Object.fromEntries(e.editables.map((x) => [x.campo, x.valor])), ...(renombres[e.ruta] ?? {}) } as Renombre
    const titulo = valores.nombrePlural ?? valores.titulo ?? valores.etiqueta ?? valores.texto ?? valores.nombre ?? e.titulo
    const arrastra = quitados.has(e.ruta) ? loQueArrastra(plan, e.ruta) : []
    return (
      <li className={clases('flex flex-col gap-2 py-2.5', nivel === 1 && 'pl-7')} key={e.ruta}>
        <div className="flex items-start gap-3">
          <Casilla className="mt-0.5" disabled={nivel === 1 && quitado && !quitados.has(e.ruta)} etiqueta={`Incluir «${titulo}»`} marcada={!quitado} onChange={() => alternar(e.ruta)} />
          <div className="min-w-0 flex-1">
            <p className={clases('text-sm font-medium', quitado && 'text-tenue line-through')}>{titulo}</p>
            {e.detalle && <p className="text-xs text-tenue">{e.detalle}</p>}
            {arrastra.length > 0 && <p className="mt-1 text-xs text-aviso">También se quita: {arrastra.join(', ')}.</p>}
          </div>
          {e.editables.length > 0 && !quitado && (
            <Boton icono={editando === e.ruta ? <Check /> : <Pencil />} onClick={() => setEditando(editando === e.ruta ? null : e.ruta)} pequeno tipo="discreto">
              {editando === e.ruta ? 'Listo' : 'Renombrar'}
            </Boton>
          )}
        </div>
        {editando === e.ruta && (
          <div className="ml-7 grid gap-2 sm:grid-cols-2">
            {e.editables.map((x) => (
              <label className="flex flex-col gap-1" key={x.campo}>
                <span className="text-xs text-tenue">{x.etiqueta}</span>
                <input
                  className={claseDeEntrada(undefined, true)}
                  maxLength={120}
                  onChange={(ev) => setRenombres((antes) => ({ ...antes, [e.ruta]: { ...(antes[e.ruta] ?? {}), [x.campo]: ev.target.value } }))}
                  value={(renombres[e.ruta]?.[x.campo] as string | undefined) ?? x.valor}
                />
              </label>
            ))}
          </div>
        )}
      </li>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      {plan.resumen && (
        <Tarjeta>
          <p className="text-sm">{plan.resumen}</p>
          <p className="mt-2 text-xs text-tenue">Todo se crea en borrador: tu web no cambia hasta que publiques. Desmarca lo que no quieras y cambia los nombres que prefieras.</p>
        </Tarjeta>
      )}

      {diferencias.length > 0 && (
        <Tarjeta>
          <h3 className="mb-2 font-display text-base font-semibold">Lo que cambia en tu web</h3>
          <ul className="flex flex-col gap-1.5">
            {diferencias.map((d) => (
              <li className="flex items-start gap-2 text-sm" key={d.ruta}>
                <Pastilla clave={d.que === 'crear' ? 'violeta' : 'neutra'}>{d.que === 'crear' ? 'Nuevo' : 'Cambia'}</Pastilla>
                <span className="min-w-0">
                  {d.etiqueta}
                  {d.detalle.length > 0 && <span className="block text-xs text-tenue">{d.detalle.join(' · ')}</span>}
                </span>
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}

      {secciones.map((s) => (
        <Tarjeta key={s.clave}>
          <h3 className="font-display text-base font-semibold">{s.titulo}</h3>
          <p className="text-xs text-tenue">{s.ayuda}</p>
          <ul className="mt-2 divide-y divide-linea">
            {s.elementos.map((e) => (
              <li key={e.ruta}>
                <ul>
                  {fila(e, 0)}
                  {e.hijos.map((h) => fila(h, 1))}
                </ul>
              </li>
            ))}
          </ul>
        </Tarjeta>
      ))}

      {vista.recortes.length > 0 && (
        <Tarjeta>
          <h3 className="mb-1 font-display text-base font-semibold">Lo que no se aplicará</h3>
          <p className="mb-2 text-xs text-tenue">La IA lo propuso, pero no cabe en tu web tal como está. No se cobra aparte ni se crea.</p>
          <ul className="flex flex-col gap-1 text-sm">
            {vista.recortes.map((r, i) => (
              <li className="flex items-start gap-2" key={`${r.ruta}-${i}`}>
                <X aria-hidden className="mt-0.5 size-3.5 shrink-0 text-tenue" />
                <span>{r.motivo}</span>
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}

      {error && <Aviso tono="error">{error}</Aviso>}
      <div className="flex flex-wrap items-center gap-2">
        <Boton cargando={enviando === 'aceptar'} disabled={enviando !== null} icono={<Check />} onClick={() => void enviar('aceptar')} tipo="primario">
          Aplicar esta estructura
        </Boton>
        <Boton cargando={enviando === 'descartar'} disabled={enviando !== null} icono={<Undo2 />} onClick={() => void enviar('descartar')}>
          {vista.origen === 'alta' ? 'No, sigue con la plantilla' : 'Descartar la propuesta'}
        </Boton>
        {quitados.size > 0 && <span className="text-xs text-tenue">{quitados.size === 1 ? 'Quitas 1 cosa.' : `Quitas ${quitados.size} cosas.`}</span>}
      </div>
    </div>
  )
}
